import React from 'react';
import { BRAND_PRESENCE_PINNED_PART_PROPS, BrandPresencePinnedAreaContext, useElementHeight } from '../useHostViewport';

/** A visible area lower than this shows no pinned bar: every pinned line costs content there. */
const PINNED_BAR_MIN_VISIBLE_HEIGHT = 640;

/** The height of the frame's own viewport, followed as the frame resizes. */
function useWindowHeight(): number {
  const [height, setHeight] = React.useState(() => window.innerHeight);

  React.useEffect(() => {
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return height;
}

/**
 * Whether the header has scrolled out of view: true once the marker at its end has left the visible
 * area upwards, that is out of view and above `midlineRef`, the middle of the visible area in the
 * frame's coordinates. Without an IntersectionObserver (tests, very old browsers) the header never pins.
 */
function useScrolledPast(markerRef: React.RefObject<HTMLElement | null>, midlineRef: React.RefObject<number>): boolean {
  const [scrolledPast, setScrolledPast] = React.useState(false);

  React.useEffect(() => {
    const marker = markerRef.current;
    if (!marker || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      // Out of view and above the middle of the visible area means scrolled past, not not-yet-reached.
      setScrolledPast(!entry.isIntersecting && entry.boundingClientRect.top < midlineRef.current);
    });
    observer.observe(marker);
    return () => observer.disconnect();
  }, [markerRef, midlineRef]);

  return scrolledPast;
}

/**
 * The compact bar pinned while the header is out of view. It tells the area how high it is while it is
 * shown, 0 while it is hidden on a small or short frame, and 0 once it goes, so the area knows how much
 * of the visible area its pinned parts cover.
 */
function PinnedHeaderBar({
  className,
  onHeight,
  children,
}: {
  className: string;
  onHeight: (height: number) => void;
  children: React.ReactNode;
}): React.ReactElement {
  const barRef = React.useRef<HTMLDivElement>(null);
  const height = useElementHeight(barRef);

  React.useEffect(() => {
    onHeight(height);
  }, [height, onHeight]);

  React.useEffect(() => () => onHeight(0), [onHeight]);

  return (
    <div ref={barRef} {...BRAND_PRESENCE_PINNED_PART_PROPS} className={className}>
      {children}
    </div>
  );
}

/**
 * The head of a Brand Presence screen: a short line of context in the typewriter face, the screen's
 * title, and an optional sentence saying what the screen is for. Actions sit on the right and wrap
 * under the title on a narrow frame.
 *
 * Once the head has scrolled away, a compact bar with the title and the actions stays pinned at the
 * top of the screen. It is laid over the content rather than taking space, so nothing jumps while
 * scrolling, and it is left out on a small or short frame, where every pinned line costs content.
 */
export function BrandPresencePageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const {
    hostScrollsFrame: scrolledByHost,
    hostTop,
    hostHeight,
    navigationHeight,
    onPinnedHeaderHeight,
  } = React.useContext(BrandPresencePinnedAreaContext);
  const windowHeight = useWindowHeight();
  // Where the host page scrolls the frame, the frame's own viewport is its whole content; the host's
  // visible area is the one the reader sees.
  const visibleTop = scrolledByHost ? hostTop : 0;
  const visibleHeight = scrolledByHost ? hostHeight : windowHeight;
  const midlineRef = React.useRef(0);
  midlineRef.current = visibleTop + visibleHeight / 2;
  const endMarkerRef = React.useRef<HTMLDivElement>(null);
  const pinned = useScrolledPast(endMarkerRef, midlineRef);
  const shortVisibleArea = visibleHeight > 0 && visibleHeight <= PINNED_BAR_MIN_VISIBLE_HEIGHT;

  // The pinned bar is a direct child of the screen's content, so the content is what it sticks within,
  // just below the area's navigation. It stacks above the content's own sticky parts, such as the first
  // column a table holds in place, which share its stacking context and come later. It takes no space
  // and no margin, so a container that spaces its children does not push the title down by a gap
  // before an empty element. Where the host page scrolls the frame, nothing in the frame sticks, so
  // the bar is placed on the host's visible line below the navigation instead.
  return (
    <>
      <div
        className={`${scrolledByHost ? 'fixed inset-x-0' : 'sticky'} z-40 m-0 h-0 max-sm:hidden ${shortVisibleArea ? 'hidden' : ''}`}
        style={{ top: hostTop + navigationHeight }}
      >
        {pinned ? (
          <PinnedHeaderBar
            onHeight={onPinnedHeaderHeight}
            className={`absolute ${scrolledByHost ? 'inset-x-0' : '-inset-x-6'} top-0 flex items-center justify-between gap-4 border-b border-slate-200/70 bg-white/85 px-6 py-2.5 shadow-sm backdrop-blur-md motion-safe:animate-bp-fade-in`}
          >
            <p className="m-0 min-w-0 truncate text-base font-bold tracking-tight text-slate-900">{title}</p>
            {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
          </PinnedHeaderBar>
        ) : null}
      </div>
      <header className="relative flex flex-wrap items-end justify-between gap-x-6 gap-y-4 mb-8 motion-safe:animate-bp-fade-up">
        <div className="min-w-0">
          {eyebrow ? <BrandPresenceEyebrow>{eyebrow}</BrandPresenceEyebrow> : null}
          <h1 className="m-0 text-[1.75rem] leading-tight font-bold tracking-tight text-slate-900">{title}</h1>
          {description ? <p className="mt-2 mb-0 max-w-2xl text-[0.9375rem] leading-relaxed text-slate-500">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        {/* The header's last line, laid over it so the marker takes no space in the screen's flow. */}
        <div ref={endMarkerRef} aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px" />
      </header>
    </>
  );
}

/** The typewriter line above a headline, led by a short stroke of the brand gradient. */
export function BrandPresenceEyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p
      className={`flex items-center gap-2 mb-2 font-eyebrow text-[0.6875rem] font-medium uppercase tracking-[0.16em] text-slate-500 ${
        className || ''
      }`}
    >
      <span aria-hidden="true" className="inline-block h-[3px] w-5 rounded-full bg-brand-gradient" />
      {children}
    </p>
  );
}
